/**
 * Shared exchange-rate & geolocation cache.
 *
 * All public pages that need currency conversion use this module instead of
 * fetching independently.  Benefits:
 *
 *  - Geolocation is cached for 24h in localStorage (a visitor's country
 *    rarely changes between visits).
 *  - Exchange rates use a short-lived in-memory cache (10 minutes) so
 *    rates stay fresh without hammering the API on every render.
 *  - In-memory promise de-duplication: if two components request the rate
 *    at the same time, only one network request is made.
 *  - Single source of truth for the geo + rate endpoints.
 */

const CACHE_KEY = 'ospito_currency_cache_v2';
const CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours — geolocation only

// Exchange rates fluctuate constantly, so we use a short in-memory TTL.
const RATE_CACHE_TTL = 10 * 60 * 1000; // 10 minutes

// ── localStorage helpers ──────────────────────────────────────────────

const getCachedCurrency = () => {
  try {
    const cached = localStorage.getItem(CACHE_KEY);
    if (cached) {
      const { data, timestamp } = JSON.parse(cached);
      if (Date.now() - timestamp < CACHE_DURATION) return data;
    }
  } catch { /* ignore */ }
  return null;
};

const setCachedCurrency = (data) => {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify({ data, timestamp: Date.now() }));
  } catch { /* ignore */ }
};

// ── In-memory promise de-duplication ──────────────────────────────────

let _fetchPromise = null;

/**
 * Fetch the visitor's geolocation (country + currency code).
 * Returns { currency, country, countryCode }.
 *
 * - Geolocation is cached for 24h in localStorage (country rarely changes).
 * - De-duplicates concurrent calls via a shared promise.
 *
 * Note: This no longer returns an exchange rate — rates are fetched
 * separately via fetchRateForCurrency() so they always stay fresh.
 */
export const fetchCurrencyData = async () => {
  // 1. Check localStorage cache (geolocation only — 24h TTL)
  const cached = getCachedCurrency();
  if (cached) return cached;

  // 2. De-duplicate: if a fetch is already in-flight, await it
  if (_fetchPromise) return _fetchPromise;

  // 3. Start a new fetch
  _fetchPromise = (async () => {
    let userCurrency = 'USD';
    let country = '';
    let countryCode = '';

    // Geolocation — try multiple providers in order (each with 6s timeout).
    // ipapi.co rate-limits aggressively, so we try ipwho.is and geojs.io first.
    const providers = [
      // ipwho.is — free, no token, returns country + currency
      async () => {
        const ctrl = new AbortController();
        const timer = setTimeout(() => ctrl.abort(), 6000);
        try {
          const res = await fetch('https://ipwho.is/', { signal: ctrl.signal });
          clearTimeout(timer);
          if (res.ok) {
            const d = await res.json();
            if (d.success !== false && d.country_code) {
              return { country: d.country || '', countryCode: d.country_code, currency: d.currency?.code || '' };
            }
          }
        } catch { clearTimeout(timer); }
        return null;
      },
      // geojs.io — free, no token, no currency but reliable country
      async () => {
        const ctrl = new AbortController();
        const timer = setTimeout(() => ctrl.abort(), 6000);
        try {
          const res = await fetch('https://get.geojs.io/v1/ip/geo.json', { signal: ctrl.signal });
          clearTimeout(timer);
          if (res.ok) {
            const d = await res.json();
            if (d.country_code) {
              return { country: d.country || '', countryCode: d.country_code, currency: '' };
            }
          }
        } catch { clearTimeout(timer); }
        return null;
      },
      // ipapi.co — original provider, but rate-limits (returns 200 + {error: true})
      async () => {
        const ctrl = new AbortController();
        const timer = setTimeout(() => ctrl.abort(), 6000);
        try {
          const res = await fetch('https://ipapi.co/json/', { signal: ctrl.signal });
          clearTimeout(timer);
          if (res.ok) {
            const d = await res.json();
            if (!d.error && d.country_code) {
              return { country: d.country_name || '', countryCode: d.country_code, currency: d.currency || '' };
            }
          }
        } catch { clearTimeout(timer); }
        return null;
      },
    ];

    for (const provider of providers) {
      const geo = await provider();
      if (geo?.countryCode) {
        country = geo.country;
        countryCode = geo.countryCode;
        if (geo.currency) userCurrency = geo.currency;
        break;
      }
    }

    // Cache geolocation only (no rate — rates are fetched fresh separately)
    const result = { currency: userCurrency, rate: 1, country, countryCode };
    setCachedCurrency(result);
    _fetchPromise = null; // clear for future refreshes
    return result;
  })();

  return _fetchPromise;
};

/**
 * Fetch the exchange rate for a specific currency.
 *
 * Uses a short-lived in-memory cache (10 minutes) so that:
 *  - Rapid re-renders don't spam the API.
 *  - Rates stay reasonably fresh during a browsing session.
 *
 * Pass { force: true } in the options to bypass the cache and always
 * fetch the latest rate from the API.
 */
const _rateMapCache = {}; // currency -> { rate, timestamp }
let _allRatesPromise = null; // de-duplicates concurrent full-rate fetches

export const fetchRateForCurrency = async (currencyCode, { force = false } = {}) => {
  if (currencyCode === 'USD') return 1;

  // Check in-memory cache (10-minute TTL)
  if (!force) {
    const cached = _rateMapCache[currencyCode];
    if (cached && Date.now() - cached.timestamp < RATE_CACHE_TTL) {
      return cached.rate;
    }
  }

  // Determine the backend API URL
  const _rawApiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
  const apiUrl = _rawApiUrl.replace('localhost', typeof window !== 'undefined' ? window.location.hostname : 'localhost');

  // Fetch from our backend endpoint, which returns exchange rates with a
  // spread so they match checkout rates. Falls back to open.er-api.com +
  // spread if the backend rate is unavailable.
  // De-duplicate concurrent calls per currency
  const promiseKey = `single_${currencyCode}`;
  if (!_allRatesPromise || force) {
    _allRatesPromise = {};
  }
  if (!_allRatesPromise[promiseKey] || force) {
    _allRatesPromise[promiseKey] = (async () => {
      try {
        const ctrl = new AbortController();
        const timer = setTimeout(() => ctrl.abort(), 10000);
        const response = await fetch(
          `${apiUrl}/api/payments/exchange-rate?currency=${currencyCode}`,
          { signal: ctrl.signal }
        );
        clearTimeout(timer);
        if (response.ok) {
          const data = await response.json();
          if (data.rate && data.rate > 0) {
            _rateMapCache[currencyCode] = { rate: data.rate, timestamp: Date.now() };
            return data.rate;
          }
        }
      } catch { /* fall through to er-api.com */ }

      // Fallback: open.er-api.com mid-market rates + 5.1% spread
      // (matches the backend's DEFAULT_SPREAD_PERCENT to approximate
      // the actual rate charged by the payment gateway at checkout)
      try {
        const SPREAD_PERCENT = 5.4;
        const ctrl2 = new AbortController();
        const timer2 = setTimeout(() => ctrl2.abort(), 8000);
        const response2 = await fetch('https://open.er-api.com/v6/latest/USD', { signal: ctrl2.signal });
        clearTimeout(timer2);
        const data2 = await response2.json();
        if (data2.rates) {
          const now = Date.now();
          for (const [code, rate] of Object.entries(data2.rates)) {
            // Apply spread to match gateway checkout rates
            const adjusted = code === 'USD' ? rate : rate * (1 + SPREAD_PERCENT / 100);
            _rateMapCache[code] = { rate: adjusted, timestamp: now };
          }
          return data2.rates[currencyCode]
            ? data2.rates[currencyCode] * (1 + SPREAD_PERCENT / 100)
            : null;
        }
      } catch { /* ignore */ }
      return null;
    })();
  }

  const rate = await _allRatesPromise[promiseKey];
  // Clean up the per-currency promise after completion
  if (_allRatesPromise[promiseKey]) {
    delete _allRatesPromise[promiseKey];
  }

  if (rate) {
    return rate;
  }

  // Fall back to stale cache if the fresh fetch failed
  const stale = _rateMapCache[currencyCode];
  return stale ? stale.rate : null;
};

export { getCachedCurrency, setCachedCurrency };
